import { Shimmer } from '@wartek-id/shimmer'
import { Text } from '@wartek-id/text-v1'

import { assetUrl } from 'utils/url'
import {
  useAPIContentKinerjaUtama,
  useAPIContentPerilakuKerja,
} from 'api/kinerja/useAPIKinerja'
import { TugasPokokItems } from 'app/KinerjaPamongPage/components/perencanaan/Detail/TugasPokokItems'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { PerencanaanPraktikKinerjaItem } from 'app/KinerjaShared/components/PerencanaanPraktikKinerjaItem'
import { PerencanaanPengembanganKompetensiItem } from 'app/KinerjaShared/components/PerencanaanPengembanganKompetensiItem'
import { PerencanaanPerilakuKerjaTable } from 'app/KinerjaShared/components/PerencanaanPerilakuKerjaTable'

import type { PerencanaanDetailData, MultiPeriodPayload } from 'types/kinerja'

export type DetailProps = {
  isLoading: boolean
  periodParams?: MultiPeriodPayload
  data: PerencanaanDetailData
  showReview?: boolean
}

export function Detail({
  isLoading,
  periodParams,
  data,
  showReview,
}: DetailProps) {
  const { periodYear, periodNo } = usePeriodContext()

  // -----------------------------------------
  // Get content data for mapping
  // -----------------------------------------
  const {
    data: dataContentKinerjaUtama,
    isLoading: isLoadingContentKinerjaUtama,
  } = useAPIContentKinerjaUtama(
    {
      tipe: 'SPNF',
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  const {
    data: dataContentPerilakuKerja,
    isLoading: isLoadingContentPerilakuKerja,
  } = useAPIContentPerilakuKerja(
    {
      tipe: 'SPNF',
      periodParams: periodParams
        ? {
            // Use period params if provided
            skp_year: periodParams.skpYear,
            skp_period: periodParams.skpPeriod,
          }
        : {
            // Use current period as fallback
            skp_year: periodYear,
            skp_period: periodNo,
          },
    },
    {
      staleTime: 6 * 60 * 60 * 1000, // 6 hour
      enabled: true,
    }
  )

  // -----------------------------------------
  // Other derivatives
  // -----------------------------------------
  const isLoadingAll =
    isLoading || isLoadingContentKinerjaUtama || isLoadingContentPerilakuKerja

  if (isLoadingAll) {
    return (
      <div className="space-y-9">
        <Shimmer height={320} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={128} width="100%" className="rounded" />
        <Shimmer height={440} width="100%" className="rounded" />
      </div>
    )
  }

  return (
    <div className="space-y-9">
      {/* Pelaksanaan Tugas Pokok */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm" as="h3">
            Pelaksanaan Tugas Pokok
          </Text>
        </div>
        <div className="space-y-4">
          <TugasPokokItems
            originalIds={data.tugasPokokIds}
            contents={dataContentKinerjaUtama?.data?.kinerjaUtama || []}
            orderNumber={1}
          />
        </div>
      </section>

      {/* Praktik Kinerja */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-badge-star.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm">Praktik Kinerja</Text>
        </div>
        <PerencanaanPraktikKinerjaItem
          originalId={data.praktikKinerjaId}
          reviewId={data.reviewPraktikKinerjaId}
          showReview={showReview}
          contents={dataContentKinerjaUtama?.data?.praktikPembelajaran || []}
          orderNumber={1}
        />
      </section>

      {/* Pengembangan Kompetensi */}
      <section>
        <div className="flex items-center gap-3 mb-4">
          <img
            src={assetUrl('/illustrations/img-document.png')}
            className="flex-shrink-0 flex-grow-0"
            alt=""
            width={52}
            height={52}
          />
          <Text variant="heading-sm">Pengembangan Kompetensi</Text>
        </div>
        <PerencanaanPengembanganKompetensiItem
          originalId={data.pengembanganKompetensiId}
          reviewId={data.reviewPengembanganKompetensiId}
          showReview={showReview}
          contents={dataContentKinerjaUtama?.data?.pengembanganKompetensi || []}
          orderNumber={1}
        />
      </section>

      {/* Perilaku Kerja */}
      <section>
        <Text variant="heading-sm" className="mb-4">
          Perilaku Kerja
        </Text>

        <PerencanaanPerilakuKerjaTable
          originalItems={data.perilakuKerjaItems}
          reviewItems={data.reviewPerilakuKerjaItems}
          showReview={showReview}
          contents={dataContentPerilakuKerja?.data?.aspek || []}
        />
      </section>
    </div>
  )
}
